/**
 * What the people an interpreter or captioner serves see instead of a name
 * (Robert, 2026-10-05): "Interpreter #1042", "Captioner #1042".
 *
 * The Deaf client, the members of a group call, the hearing party, a VRI
 * customer and their guests see the ID, never the real name. Staff surfaces
 * keep the real name: the admin dashboard, ops, activity logs, transfers and
 * team messages between interpreters, Jobs, payroll, and the interpreter's own
 * app.
 *
 * The number is interpreters.public_number / captioners.public_number
 * (migration 000219): one sequence for both tables, from 1001. Without a
 * number the label is generic ("Your interpreter"), never the name.
 *
 * Plain CommonJS with a browser global, so the Node server can require it and
 * both app bundles can import it. Keep it dependency-free.
 */
(function(root, factory) {
    const labels = factory();
    if (typeof module === 'object' && module.exports) module.exports = labels;
    else root.MalkaProviderLabel = labels;
})(typeof globalThis !== 'undefined' ? globalThis : this, function() {
    const INTERPRETER_FALLBACK = 'Your interpreter';
    const CAPTIONER_FALLBACK = 'Your captioner';

    /**
     * A public number as stored: a positive safe integer, or null.
     *
     * @param {unknown} value - A number, or a numeric string from a JSON row.
     * @returns {number|null}
     */
    function normalizePublicNumber(value) {
        if (value === null || value === undefined || value === '' || typeof value === 'boolean') return null;
        const number = typeof value === 'number' ? value : Number(String(value).trim());

        return Number.isSafeInteger(number) && number > 0 ? number : null;
    }

    function labelWith(word, fallback, value) {
        const number = normalizePublicNumber(value);

        return number === null ? fallback : `${word} #${number}`;
    }

    /** "Interpreter #1042", or "Your interpreter" without a number. */
    function interpreterLabel(value) {
        return labelWith('Interpreter', INTERPRETER_FALLBACK, value);
    }

    /** "Captioner #1042", or "Your captioner" without a number. */
    function captionerLabel(value) {
        return labelWith('Captioner', CAPTIONER_FALLBACK, value);
    }

    /** True for the roles that are shown by ID to the people they serve. */
    function isProviderRole(role) {
        return role === 'interpreter' || role === 'captioner';
    }

    /**
     * The label for a provider role, or null for any other role.
     *
     * @param {string} role - 'interpreter' or 'captioner'.
     * @param {unknown} value - The public number.
     * @returns {string|null}
     */
    function providerLabel(role, value) {
        if (role === 'interpreter') return interpreterLabel(value);
        if (role === 'captioner') return captionerLabel(value);

        return null;
    }

    return {
        CAPTIONER_FALLBACK,
        INTERPRETER_FALLBACK,
        captionerLabel,
        interpreterLabel,
        isProviderRole,
        normalizePublicNumber,
        providerLabel
    };
});
